<template>
    <v-container fluid class="pa-4 pa-md-6">
        <v-btn variant="text" prepend-icon="mdi-arrow-left" class="mb-3" @click="back">Back to compliance</v-btn>

        <template v-if="loading">
            <v-skeleton-loader type="card, list-item-two-line@3"/>
        </template>

        <template v-else-if="detail">
            <div class="d-flex align-start justify-space-between mb-4">
                <div>
                    <h1 class="text-h5 font-weight-bold">{{ detail.asset_name || ('Asset ' + assetId) }}</h1>
                    <div class="text-body-2 text-medium-emphasis">VIN {{ detail.vin || 'unknown' }}</div>
                </div>
                <v-menu v-if="canCancel && enrolled">
                    <template #activator="{props}">
                        <v-btn icon="mdi-dots-vertical" variant="text" v-bind="props"/>
                    </template>
                    <v-list density="compact">
                        <v-list-item prepend-icon="mdi-close-circle-outline" title="Cancel enrollment"
                                     @click="confirmCancel = true"/>
                    </v-list>
                </v-menu>
            </div>

            <v-card rounded="lg" class="mb-4" :color="statusColor(detail.severity)" variant="tonal">
                <v-card-text class="d-flex align-center flex-wrap ga-4">
                    <v-icon :icon="statusIcon(detail.severity)" size="40"/>
                    <div class="flex-grow-1">
                        <div class="text-h6 font-weight-bold">{{ detail.status_label }}</div>
                        <div class="text-body-2">{{ detail.explanation }}</div>
                    </div>
                    <div class="text-right">
                        <v-chip :color="deadlineColor(detail.days_to_deadline)" variant="flat" class="mb-2">
                            {{ deadlineLabel(detail.days_to_deadline) }}
                        </v-chip>
                        <div>
                            <v-btn v-if="enrolled && canAct" color="primary" size="small" prepend-icon="mdi-radar"
                                   :disabled="detail.harness_compatible === false || !detail.vin"
                                   :loading="scanning" @click="requestScan">Scan at next ignition</v-btn>
                            <v-btn v-if="enrolled && canAct" color="primary" size="small" variant="text"
                                   prepend-icon="mdi-cloud-upload-outline" class="mt-1"
                                   :disabled="detail.harness_compatible === false"
                                   :loading="reuploading" @click="requestReupload">Re-upload last capture</v-btn>
                            <div v-if="enrolled && canAct && detail.harness_compatible !== false && !detail.vin"
                                 class="text-caption text-warning mt-1">Set the vehicle VIN on the asset to enable scans</div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>

            <v-alert v-if="enrolled && detail.harness_compatible === false" type="warning" variant="tonal"
                     class="mb-4" border="start" icon="mdi-leaf-off">
                This vehicle's device cannot perform Clean Truck Check scans - its SKU is not CARB-capable. Assign a
                CARB-capable device to collect compliance data.
            </v-alert>

            <v-card rounded="lg" class="mb-4">
                <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
                    Vehicle details
                    <v-spacer/>
                    <v-btn variant="text" size="small" prepend-icon="mdi-pencil"
                           @click="goToAsset">Edit on asset</v-btn>
                </v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">VIN</div>
                            <div>{{ detail.vin || '-' }}</div>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">License plate</div>
                            <div>{{ detail.license_plate || '-' }}</div>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">Owner email</div>
                            <div>{{ detail.owner_email || '-' }}</div>
                        </v-col>
                    </v-row>
                    <div class="text-caption text-medium-emphasis mt-3">
                        VIN, license plate and owner email are managed on the asset.
                    </div>
                </v-card-text>
            </v-card>

            <v-row>
                <v-col cols="12" md="6">
                    <v-card rounded="lg" class="h-100">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Readiness monitors</v-card-title>
                        <v-card-text>
                            <template v-if="monitors.length">
                                <div v-for="m in monitors" :key="m.name" class="d-flex align-center justify-space-between py-1">
                                    <span>{{ m.name }}</span>
                                    <v-chip size="x-small" :color="m.ready ? 'success' : 'warning'" variant="tonal">
                                        <v-icon start size="x-small" :icon="m.ready ? 'mdi-check' : 'mdi-clock-outline'"/>
                                        {{ m.ready ? 'Ready' : 'Not ready' }}
                                    </v-chip>
                                </div>
                            </template>
                            <div v-else class="text-medium-emphasis py-2">No readiness data yet.</div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card rounded="lg" class="h-100">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Fault codes</v-card-title>
                        <v-card-text>
                            <div class="d-flex align-center mb-2">
                                <span class="mr-2">Check engine (MIL)</span>
                                <v-chip size="x-small" :color="dtc?.mil ? 'error' : 'success'" variant="tonal">
                                    {{ dtc?.mil ? 'On' : 'Off' }}
                                </v-chip>
                            </div>
                            <template v-if="dtc?.active?.length">
                                <div v-for="(code, i) in dtc.active" :key="i" class="py-1">
                                    <span class="font-weight-medium">SPN {{ code.spn }} / FMI {{ code.fmi }}</span>
                                    <span class="text-medium-emphasis"> - {{ code.description }}</span>
                                </div>
                            </template>
                            <div v-else class="text-medium-emphasis py-1">No active fault codes.</div>
                            <div class="text-caption text-medium-emphasis mt-2">
                                Pending {{ dtc?.pending ?? 0 }} &middot; Permanent {{ dtc?.permanent ?? 0 }}
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-card rounded="lg" class="mt-4">
                <v-card-title class="text-subtitle-1 font-weight-bold">Session history</v-card-title>
                <v-card-subtitle v-if="submissions.length" class="pb-0">Select a session to view its decoded report.</v-card-subtitle>
                <v-list v-if="submissions.length" density="comfortable">
                    <v-list-item v-for="s in submissions" :key="s.id" link @click="openReport(s)">
                        <template #prepend>
                            <span class="text-caption font-weight-medium text-medium-emphasis text-right mr-3" style="min-width: 96px;">#{{ s.record_id ?? '-' }}</span>
                            <v-icon :color="carbColor(s.carb_status)" :icon="carbIcon(s.carb_status)"/>
                        </template>
                        <v-list-item-title>
                            {{ s.submitted_at ? formatDateTime(s.submitted_at) : 'Collected ' + formatDateTime(s.collected_at) }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            CARB: {{ s.carb_status || 'pending' }}
                            <span v-if="s.receipt_hash"> &middot; receipt {{ s.receipt_hash }}</span>
                        </v-list-item-subtitle>
                        <template #append>
                            <v-chip size="x-small" variant="tonal" class="mr-2">{{ s.transport_status }}</v-chip>
                            <v-icon icon="mdi-chevron-right" size="small"/>
                        </template>
                    </v-list-item>
                </v-list>
                <v-card-text v-else class="text-medium-emphasis">No sessions yet.</v-card-text>
            </v-card>
        </template>

        <v-alert v-else type="error" variant="tonal">Compliance data could not be loaded for this asset.</v-alert>

        <v-dialog v-model="confirmCancel" max-width="440">
            <v-card rounded="lg">
                <v-card-title class="text-h6">Cancel enrollment?</v-card-title>
                <v-card-text>
                    This removes the vehicle from Clean Truck Check tracking. It will not be scanned or
                    submitted to CARB until it is enrolled again.
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn variant="text" @click="confirmCancel = false">Keep enrolled</v-btn>
                    <v-btn color="error" :loading="cancelling" @click="cancelEnrollment">Cancel enrollment</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="reportOpen" max-width="900" scrollable>
            <v-card rounded="lg">
                <v-card-title class="d-flex align-center">
                    <span class="text-h6">Session report</span>
                    <v-spacer/>
                    <v-btn icon="mdi-close" variant="text" size="small" @click="reportOpen = false"/>
                </v-card-title>
                <v-divider/>
                <v-card-text style="max-height: 70vh">
                    <div v-if="reportLoading" class="text-center py-8"><v-progress-circular indeterminate/></div>
                    <template v-else-if="report">
                        <div class="d-flex flex-wrap ga-4 mb-3">
                            <div><div class="text-caption text-medium-emphasis">Record</div><div>#{{ report.record_id ?? '-' }}</div></div>
                            <div><div class="text-caption text-medium-emphasis">Outcome</div><div>{{ report.outcome }}</div></div>
                            <div><div class="text-caption text-medium-emphasis">CARB result</div><div>{{ report.carb_status || 'pending' }}</div></div>
                            <div><div class="text-caption text-medium-emphasis">Collected</div><div>{{ report.collected_at ? formatDateTime(report.collected_at) : '-' }}</div></div>
                            <div><div class="text-caption text-medium-emphasis">Submitted</div><div>{{ report.submitted_at ? formatDateTime(report.submitted_at) : 'Not submitted' }}</div></div>
                        </div>
                        <div class="mb-4">
                            <v-btn size="small" variant="tonal" prepend-icon="mdi-file-delimited-outline" class="mr-2"
                                   :disabled="!report.has_files" :loading="downloading === 'csv'" @click="downloadSession('csv')">CSV</v-btn>
                            <v-btn size="small" variant="tonal" prepend-icon="mdi-xml"
                                   :disabled="!report.has_files" :loading="downloading === 'xml'" @click="downloadSession('xml')">XML</v-btn>
                        </div>

                        <v-alert v-if="!report.ecus || !report.ecus.length" type="info" variant="tonal" density="compact" class="mb-2">
                            No decoded ECU data (outcome: {{ report.outcome }}).
                        </v-alert>

                        <div v-for="(ecu, ei) in (report.ecus || [])" :key="ei" class="mb-4">
                            <div class="text-subtitle-2 font-weight-bold">
                                {{ ecu.name || 'ECU' }}
                                <v-chip size="x-small" class="ml-2" :color="ecu.mil === 'on' ? 'error' : 'success'" variant="tonal">MIL {{ ecu.mil || 'off' }}</v-chip>
                                <span class="text-caption text-medium-emphasis ml-2">addr {{ ecu.address }}</span>
                            </div>
                            <div v-for="(g, gi) in (ecu.groups || [])" :key="gi" class="mt-2">
                                <div class="text-body-2 font-weight-medium">{{ g.comment || g.semantic }}</div>
                                <v-table density="compact">
                                    <tbody>
                                        <tr v-for="(r, ri) in groupRows(g)" :key="ri">
                                            <td class="text-medium-emphasis">{{ r.comment || r.name }}</td>
                                            <td>{{ r.value }}<span v-if="r.unit" class="text-medium-emphasis"> {{ r.unit }}</span></td>
                                        </tr>
                                    </tbody>
                                </v-table>
                            </div>
                        </div>

                        <v-expansion-panels class="mt-2">
                            <v-expansion-panel :title="`Raw frames (${(report.frames || []).length})`">
                                <v-expansion-panel-text>
                                    <v-table density="compact">
                                        <thead><tr><th>Time</th><th>Type</th><th>CAN ID</th><th>Data</th></tr></thead>
                                        <tbody>
                                            <tr v-for="(f, fi) in (report.frames || [])" :key="fi">
                                                <td class="text-caption">{{ f.timestamp }}</td>
                                                <td>{{ f.type }}</td>
                                                <td><code>{{ f.can_id }}</code></td>
                                                <td><code>{{ f.data }}</code></td>
                                            </tr>
                                        </tbody>
                                    </v-table>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {formatDateTime} from '@/composables/useTimezone';
import {useCtcStatus} from '@/composables/useCtcStatus';
import {canActOnCompliance, canManageComplianceEnrollment} from '@/composables/globals';
import {useAuthStore} from '@/stores/authStore';

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const {user} = useAuthStore();
const {statusColor, statusIcon, deadlineColor, deadlineLabel} = useCtcStatus();

const assetId = computed(() => route.params.asset);
const canAct = computed(() => canActOnCompliance(user));
const canCancel = computed(() => canManageComplianceEnrollment(user));

const loading = ref(true);
const scanning = ref(false);
const confirmCancel = ref(false);
const cancelling = ref(false);
const detail = ref(null);
const submissions = ref([]);

const enrolled = computed(() => detail.value && detail.value.enrollment_state && detail.value.enrollment_state !== 'not_enrolled');
const latest = computed(() => submissions.value[0] ?? null);
const monitors = computed(() => latest.value?.monitors ?? []);
const dtc = computed(() => latest.value?.dtc_summary ?? null);

const carbColor = (status) => {
    if (status === 'pass') return 'success';
    if (status === 'fail') return 'error';
    if (!status) return 'info';
    return 'warning';
};
const carbIcon = (status) => {
    if (status === 'pass') return 'mdi-check-circle';
    if (status === 'fail') return 'mdi-alert-octagon';
    if (!status) return 'mdi-progress-clock';
    return 'mdi-alert';
};

const back = () => router.push({name: 'ctc-compliance'});

const load = () => {
    loading.value = true;
    Promise.all([
        send('get', `ctc/assets/${assetId.value}`),
        send('get', `ctc/assets/${assetId.value}/submissions`),
    ]).then(([detailRes, subsRes]) => {
        detail.value = detailRes.data.data;
        submissions.value = subsRes.data.data || [];
    }).catch(() => {
        detail.value = null;
        showSnackbar('Compliance data could not be loaded', 'error');
    }).finally(() => loading.value = false);
};

const requestScan = () => {
    scanning.value = true;
    send('post', `ctc/assets/${assetId.value}/scan`)
        .then((res) => {
            if (res.data.success === false) {
                showSnackbar(res.data.message || 'Could not request a scan', 'error');
                return;
            }
            showSnackbar(res.data.message || 'Scan requested', 'success');
        })
        .catch((err) => {
            const msg = err?.response?.status === 403
                ? 'You do not have permission to request a scan'
                : 'Could not request a scan';
            showSnackbar(msg, 'error');
        })
        .finally(() => scanning.value = false);
};

const reuploading = ref(false);
const requestReupload = () => {
    reuploading.value = true;
    send('post', `ctc/assets/${assetId.value}/reupload`)
        .then((res) => {
            if (res.data.success === false) {
                showSnackbar(res.data.message || 'Could not request a re-upload', 'error');
                return;
            }
            showSnackbar(res.data.message || 'Re-upload requested', 'success');
        })
        .catch((err) => {
            const msg = err?.response?.status === 403
                ? 'You do not have permission to request a re-upload'
                : 'Could not request a re-upload';
            showSnackbar(msg, 'error');
        })
        .finally(() => reuploading.value = false);
};

const cancelEnrollment = () => {
    cancelling.value = true;
    send('delete', `ctc/assets/${assetId.value}`)
        .then((res) => {
            if (res.data.success === false) {
                showSnackbar(res.data.message || 'Could not cancel enrollment', 'error');
                return;
            }
            showSnackbar('Enrollment cancelled', 'success');
            router.push({name: 'ctc-compliance'});
        })
        .catch((err) => {
            const msg = err?.response?.status === 403
                ? 'You do not have permission to cancel enrollment'
                : 'Could not cancel enrollment';
            showSnackbar(msg, 'error');
        })
        .finally(() => {
            cancelling.value = false;
            confirmCancel.value = false;
        });
};

const goToAsset = () => router.push({name: 'asset', params: {asset: assetId.value}});

const reportOpen = ref(false);
const reportLoading = ref(false);
const report = ref(null);
const reportSubId = ref(null);
const downloading = ref('');

const openReport = (s) => {
    reportSubId.value = s.id;
    report.value = null;
    reportOpen.value = true;
    reportLoading.value = true;
    send('get', `ctc/assets/${assetId.value}/submissions/${s.id}`)
        .then((res) => {
            report.value = res.data?.data ?? null;
        })
        .catch(() => showSnackbar('Could not load the session report', 'error'))
        .finally(() => reportLoading.value = false);
};

const groupRows = (g) => {
    const rows = [];
    (g.measurements || []).forEach((m) => rows.push(m));
    (g.pgn_groups || []).forEach((pg) => {
        (pg.measurements || []).forEach((m) => rows.push(m));
        (pg.sets || []).forEach((set) => (set.measurements || []).forEach((m) => rows.push(m)));
    });
    return rows;
};

const downloadSession = (kind) => {
    if (!reportSubId.value) return;
    downloading.value = kind;
    send('get', `ctc/assets/${assetId.value}/submissions/${reportSubId.value}/${kind}`)
        .then((res) => {
            const data = res.data;
            if (data && typeof data === 'object' && data.success === false) {
                showSnackbar(data.message || 'No file available for this session', 'error');
                return;
            }
            const text = typeof data === 'string' ? data : JSON.stringify(data);
            const blob = new Blob([text], {type: kind === 'csv' ? 'text/csv' : 'application/xml'});
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `carb-session-${reportSubId.value}.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        })
        .catch(() => showSnackbar(`Could not download the ${kind.toUpperCase()}`, 'error'))
        .finally(() => downloading.value = '');
};

watch(assetId, load, {immediate: true});
</script>
